import { useEffect, useRef } from "react";

import { sectionSelector } from "@/components/overview/overview-sections";
import {
  referenceOrder,
  scrollToNamed,
  unmarkSections,
  useReferencedTodos,
  type ReferencedTodos,
} from "@/components/todos/tray/use-referenced-todos";
import { MOBILE_BREAKPOINT } from "@/hooks/use-mobile";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { pointNumber, stillMarked } from "@/lib/kory-focus";
import { isTarget, parseTarget } from "@/lib/kory-pointing";
import { isPagePart, refSelector } from "@/lib/kory-refs";
import { hiddenFromView, visibleBox } from "@/lib/kory-screen-digest";

/** The mark on a pointed element: Kory's number, or empty for the leaf. `index.css` draws the ring and chip. */
export const RING_ATTR = "data-kory-ring";
// A reply's first thing may still be loading (a feed behind a fetch): it scrolls into view once it shows.
const SCROLL_WAIT_MS = 10_000;
/** What can show, hide or swap a pointed thing without adding or removing a node. */
const WATCHED_ATTRIBUTES = ["data-kory-ref", "data-kory-pointed", "data-overview-section", "class", "style", "hidden"];
/** A Kory tray (bottom sheet, floating panel or side pane). */
const TRAY = "[data-detent]";
/** A table row's ring goes on its label cell: a row's own `::before` takes a cell's place and shifts the row,
 *  and its sticky lead cells would cover a ring drawn around it. */
const RING_CELL = ":scope > [data-kory-ring-cell]";
/** Set beside the mark on a thing that draws its ring on itself, with no chip (`drawsOnItself`). */
export const FRAME_ATTR = "data-kory-ring-frame";
/** Elements a browser draws no `::before` or `::after` on. */
const NO_PSEUDO = "img, input, select, textarea, video, audio, iframe, canvas, embed, object, svg";

/** The elements that carry the mark for a pointed element: itself, or a table row's label cells, else the
 *  row's first cell shown. */
function ringHosts(el: HTMLElement): HTMLElement[] {
  if (!(el instanceof HTMLTableRowElement)) return [el];
  const labels = [...el.querySelectorAll<HTMLElement>(RING_CELL)];
  if (labels.length) return labels;
  const first = [...el.cells].find((cell) => !hiddenFromView(cell));
  return first ? [first] : [];
}

/** A thing that cannot hold the ring's `::before` and the chip's `::after` (an image, a form control), or one
 *  that scrolls its own content, which would scroll them away (and must keep scrolling): its ring and glow are
 *  drawn on itself, with no chip. */
function drawsOnItself(el: Element): boolean {
  if (el.matches(NO_PSEUDO)) return true;
  const { overflowX, overflowY } = getComputedStyle(el);
  return /auto|scroll/.test(`${overflowX} ${overflowY}`);
}

function unmark(el: HTMLElement) {
  el.removeAttribute(RING_ATTR);
  el.removeAttribute(FRAME_ATTR);
}

type Pointed = { entry: string; n: number | null };

/** What the mark points at, in Kory's numbering: its cards, records, page parts and targets while still
 *  marked, then the Overview sections it names (never numbered). Uncapped here: the backend caps each kind
 *  on its own, and everything it keeps is numbered, so everything it keeps is ringed. */
function pointedEntries(mark: ReferencedTodos): Pointed[] {
  const order = [
    ...mark.order,
    ...referenceOrder(mark.ids, mark.nouns, mark.sections).filter((entry) => !mark.order.includes(entry)),
  ].filter((entry) => stillMarked(mark, entry));
  const sections = mark.sections.filter((entry) => !isTarget(entry));
  return [
    ...order.map((entry) => ({ entry, n: pointNumber(mark, [entry]) })),
    ...sections.map((entry) => ({ entry, n: null })),
  ];
}

/** Every element on the page that shows ``entry``: a target `usePointedTargets` marked, an Overview
 *  section's root, or each element carrying its ref (`data-kory-ref`). */
function resolvePointed(entry: string): HTMLElement[] {
  const selector = parseTarget(entry)
    ? `[data-kory-pointed="${CSS.escape(entry)}"]`
    : !isTarget(entry) && !entry.includes(":")
      ? sectionSelector(entry)
      : refSelector(entry);
  return [...document.querySelectorAll<HTMLElement>(selector)].filter((el) => !hiddenFromView(el));
}

/** The first thing a reply names, on the page and not all in view (off screen, or clipped by a scroller it
 *  sits in, like a Win the Day check to the right of the ribbon): brought into view gently, once. A target
 *  and an Overview section scroll themselves (`usePointedTargets`, `useRevealSections`), and a to-do in a
 *  tray is the tray's. On a phone the tray covers the page, and its strip lists the rest instead. */
function revealFirst(first: Pointed | undefined, reduce: boolean): boolean {
  if (!first || !first.entry.includes(":") || parseTarget(first.entry)) return true;
  if (window.innerWidth < MOBILE_BREAKPOINT) return true;
  const el = resolvePointed(first.entry).find((found) => !found.closest(TRAY));
  if (!el) return false;
  const box = el.getBoundingClientRect();
  const shown = visibleBox(el);
  // A sliver showing is not in view: what shows must be the whole of it, or all a window's height of it.
  const whole =
    shown &&
    shown.right - shown.left >= box.width - 1 &&
    shown.bottom - shown.top >= Math.min(box.height, window.innerHeight) - 1;
  if (!whole) scrollToNamed(el, reduce);
  return true;
}

/**
 * Kory pointing, once for every page: each thing Kory's latest reply names carries `data-kory-ring`, and
 * `index.css` draws on the thing itself an unmissable gold ring with a soft gold glow, and an 18px gold chip
 * at its top-right corner with Kory's number (two or more things), or Kory's leaf (one thing, or an Overview
 * section). Being part of the thing, the ring moves with it in the same frame on any scroll, swipe or
 * resize. A surface only carries the refs of what it shows (`data-kory-ref`); this finds them, so no surface
 * can forget its mark, and marks again, at most once a frame, when the page adds, swaps or shows a pointed
 * thing. It brings the first thing into view once per reply, for up to 10 s. Marks clear by the store's
 * rules: Kory lets go, you act on it (touching a page part), the tray folds, or a new reply names others.
 * Mounted once, in the app shell.
 */
export function KoryPointingLayer() {
  const mark = useReferencedTodos();
  const reduce = usePrefersReducedMotion();
  const scrolled = useRef(0);
  const entries = pointedEntries(mark);
  const key = entries.map(({ entry, n }) => `${n ?? ""}\t${entry}`).join("\n");
  const { nonce } = mark;

  useEffect(() => {
    if (!key) return;
    const pointed: Pointed[] = key.split("\n").map((line) => {
      const [n, entry] = line.split("\t");
      return { entry, n: n ? Number(n) : null };
    });
    let frame = 0;
    let marked = new Map<HTMLElement, string>();
    const place = () => {
      frame = 0;
      if (scrolled.current !== nonce && revealFirst(pointed[0], reduce)) scrolled.current = nonce;
      const next = new Map<HTMLElement, string>();
      for (const { entry, n } of pointed) {
        for (const el of resolvePointed(entry).flatMap(ringHosts)) if (!next.has(el)) next.set(el, n == null ? "" : String(n));
      }
      for (const el of marked.keys()) if (!next.has(el)) unmark(el);
      for (const [el, ring] of next) {
        // Read before the first mark: once marked, index.css shows the thing's overflow, so it reads as no scroller.
        if (!el.hasAttribute(RING_ATTR)) el.toggleAttribute(FRAME_ATTR, drawsOnItself(el));
        if (el.getAttribute(RING_ATTR) !== ring) el.setAttribute(RING_ATTR, ring);
      }
      marked = next;
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(place);
    };
    place();
    // Past the wait, the first thing never scrolls into view: the person has moved on.
    const stopRevealing = setTimeout(() => {
      scrolled.current = nonce;
    }, SCROLL_WAIT_MS);
    const changed = new MutationObserver(schedule);
    changed.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: WATCHED_ATTRIBUTES });
    // Touching a page part Kory points at is acting on it: its mark goes, as a target's does.
    const parts = pointed.filter(({ entry }) => isPagePart(entry)).map(({ entry }) => entry);
    const touch = (event: Event) => {
      const touched = parts.filter((entry) => resolvePointed(entry).some((el) => el.contains(event.target as Node)));
      if (touched.length) unmarkSections(touched);
    };
    document.addEventListener("pointerdown", touch, true);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(stopRevealing);
      changed.disconnect();
      document.removeEventListener("pointerdown", touch, true);
      for (const el of marked.keys()) unmark(el);
    };
  }, [key, nonce, reduce]);

  return null;
}
